import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { focusIntoWhenPaintable, restoreFocus, trapTabKey } from '../a11y/focus';
import { useEscapeLayer, useOutsideClick } from '../a11y/layers';
import { usePosition, type Placement } from '../a11y/usePosition';
import { cx } from '../cx';
import styles from './Popover.module.css';

// Popover — DESIGN_SYSTEM §12 row 32 (`.popover`): an anchored, focus-managed panel for a decision or a small
// form (the confirm before turning off, the budget editor). Controlled: the caller owns `open` and the trigger
// (give the trigger `aria-expanded` and `aria-haspopup="dialog"`). On open, focus moves inside (the first
// tabbable, or `initialFocusRef`); Tab cycles inside; Escape and a click outside close it; on close, focus
// returns to the anchor (TEST_PLAN §1.6 K6). Rendered in a portal at the end of <body>, positioned against
// the anchor and flipped when there is no room (a11y/usePosition).

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** The trigger (or any element) the panel hangs off. Focus returns here on close. */
  anchorRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  /** Heading inside the panel; also the dialog's accessible name. Otherwise pass `aria-label`. */
  title?: string;
  'aria-label'?: string;
  children: ReactNode;
  /** Right-aligned buttons ("Keep it on" · "Stage this change"). */
  footer?: ReactNode;
  /** Focus lands here on open instead of the first tabbable element. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Focus returns here on close instead of the anchor. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Fixed width (px or CSS length); default: the content's width, at most 360 px. */
  width?: number | string;
  /** Test id of the panel. */
  testID?: string;
  className?: string;
  id?: string;
}

export function Popover({
  open,
  onClose,
  anchorRef,
  placement = 'bottom-start',
  title,
  children,
  footer,
  initialFocusRef,
  returnFocusRef,
  width,
  testID,
  className,
  id,
  ...labelling
}: PopoverProps) {
  if (import.meta.env.DEV && !title && !labelling['aria-label']) {
    throw new Error('Popover needs a title or an aria-label (REQ-281).');
  }
  const generated = useId();
  const titleId = `${id ?? generated}-title`;
  const panelRef = useRef<HTMLDivElement>(null);
  const position = usePosition({ open, anchorRef, floatingRef: panelRef, placement });
  /**
   * **BUG-020.** `usePosition` paints the panel `visibility: hidden` for one commit while it measures the
   * anchor, and an element inside `visibility: hidden` is not focusable in a real browser — `.focus()` is a
   * no-op there. So the first attempt did nothing, the trigger kept the focus, and the panel (a `<body>`
   * portal) sat 13 Tab stops behind the page. jsdom has no layout and focuses it happily, which is why every
   * unit test passed and only QA's Playwright K3 walk found it. Focus therefore waits for the position.
   */
  const positioned = position.style.visibility !== 'hidden';

  useEscapeLayer(open, onClose);
  useOutsideClick(open, [panelRef, anchorRef], onClose);

  // Focus in, once, as soon as the panel is really on screen — and `focusIntoWhenPaintable` looks to see
  // whether it landed rather than assuming it did (BUG-020).
  const tookFocus = useRef(false);
  useEffect(() => {
    if (!open) {
      tookFocus.current = false;
      return;
    }
    if (!positioned || tookFocus.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    tookFocus.current = true;
    return focusIntoWhenPaintable(panel, initialFocusRef?.current);
    // The refs are stable objects; `open` and `positioned` re-run this.
  }, [open, positioned, initialFocusRef]);

  // Focus back to the trigger on close or unmount — its own effect, so the one above can be re-run by
  // `positioned` without handing the focus back in between.
  useEffect(() => {
    if (!open) return;
    return () => {
      restoreFocus(returnFocusRef?.current ?? anchorRef.current);
    };
  }, [open, anchorRef, returnFocusRef]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (panelRef.current) trapTabKey(event, panelRef.current);
  };

  // `aria-modal` (T-076-W1): the panel TRAPS Tab, so assistive tech must not be told that the page behind it
  // is still reachable. `Modal` and the phone's `Sheet` both declare it; this and the pinned `Explainer` were
  // the two dialog-ish primitives that trapped without saying so.
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      id={id}
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={labelling['aria-label']}
      tabIndex={-1}
      className={cx(styles.panel, className)}
      style={{ ...position.style, width }}
      data-side={position.side}
      data-testid={testID}
      onKeyDown={onKeyDown}
    >
      {title ? (
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
      ) : null}
      {children}
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>,
    document.body,
  );
}
